import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Button,
  Empty,
  Input,
  Modal,
  Popconfirm,
  Select,
  Space,
  Spin,
  Table,
  Tabs,
  Tag,
  Tooltip,
} from "antd";
import {
  CopyOutlined,
  DeleteOutlined,
  EditOutlined,
  EyeOutlined,
  PoweroffOutlined,
  PlusOutlined,
  UserOutlined,
} from "@ant-design/icons";
import Swal from "sweetalert2";
import usePermissions from "../../../hook/usePermissions";
import {
  ActualizarRolAdmin,
  CrearRolAdmin,
  EliminarRolAdmin,
  ListarCatalogoPermisos,
  ListarRolesAdmin,
  ListarUsuariosRol,
  ObtenerPermisosEfectivosUsuario,
  ObtenerRolAdmin,
  OtorgarPermisoExtra,
  ReemplazarPermisosRol,
  RevocarPermisoExtra,
} from "../../../Service/Service.roles";
import { ListarClinicas } from "../../../Service/Serive.clinica";
import RoleForm from "./RoleForm";
import RolePermissionsMatrix from "./RolePermissionsMatrix";
import RoleUsers from "./RoleUsers";
import {
  canChangeCustomRole,
  getRoleTypeLabel,
  isSystemLockedRole,
} from "./rolesPermissionsUtils";
import "../../../styles/roles-permissions.css";

const getApiError = (error, fallback) =>
  error?.response?.data?.message || error?.message || fallback;

const roleTypeColor = (role) => {
  if (role.is_transition) return "gold";
  if (role.is_system) return "blue";
  return "green";
};

export default function RolesIndex() {
  const { loading: permissionsLoading, hasPermission } = usePermissions();
  const canView = hasPermission("roles.view");
  const canEdit = hasPermission("roles.edit");
  const canDelete = hasPermission("roles.delete");
  // RBAC-USER-EXTRAS-02: separa "editar la PLANTILLA de un rol"
  // (roles.assign_permissions, tab Permisos) de "administrar permisos extra
  // ALLOW de UN usuario puntual" (users.manage_permission_extras, tab
  // Usuarios) -- antes ambas dependian del mismo flag.
  const canAssignPermissions = hasPermission("roles.assign_permissions");
  const canManageUserExtras = hasPermission("users.manage_permission_extras");
  // RBAC-CUSTOM-ROLES-03B seccion D/N: gates DEDICADOS para roles
  // PERSONALIZADOS por clinica -- nunca se reutilizan roles.edit/delete/
  // assign_permissions (esos siguen significando exclusivamente
  // "administrar plantillas Sistema").
  const canCreateCustom = hasPermission("roles.custom.create");
  const canEditCustom = hasPermission("roles.custom.edit");
  const canDeleteCustom = hasPermission("roles.custom.delete");
  const canAssignPermissionsCustom = hasPermission("roles.custom.assign_permissions");

  const [roles, setRoles] = useState([]);
  const [authorizedClinics, setAuthorizedClinics] = useState([]);
  const [clinicsError, setClinicsError] = useState("");
  const [copySource, setCopySource] = useState(null);
  const [catalog, setCatalog] = useState([]);
  const [selectedRoleId, setSelectedRoleId] = useState(null);
  const [roleDetail, setRoleDetail] = useState(null);
  const [detailModal, setDetailModal] = useState({ open: false, mode: "view" });
  const [detailTab, setDetailTab] = useState("permissions");
  const [nombreEdit, setNombreEdit] = useState("");
  const [descripcionEdit, setDescripcionEdit] = useState("");
  const [selectedPermissionIds, setSelectedPermissionIds] = useState([]);
  const [initialPermissionIds, setInitialPermissionIds] = useState([]);
  const [searchRole, setSearchRole] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [loadingInitial, setLoadingInitial] = useState(false);
  const [initialError, setInitialError] = useState("");
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState("");
  const [matrixError, setMatrixError] = useState("");
  const [savingPermissions, setSavingPermissions] = useState(false);
  const [formState, setFormState] = useState({ open: false, mode: "create", role: null });
  const [formLoading, setFormLoading] = useState(false);
  const [formError, setFormError] = useState("");
  const [users, setUsers] = useState([]);
  const [usersLoadedRoleId, setUsersLoadedRoleId] = useState(null);
  const [usersLoading, setUsersLoading] = useState(false);
  const [usersError, setUsersError] = useState("");
  const [selectedUser, setSelectedUser] = useState(null);
  const [effectiveData, setEffectiveData] = useState(null);
  const [effectiveLoading, setEffectiveLoading] = useState(false);
  const [effectiveError, setEffectiveError] = useState("");
  const [grantLoading, setGrantLoading] = useState(false);
  const [revokeLoadingName, setRevokeLoadingName] = useState("");
  const initialLoadedRef = useRef(false);

  const permissionsDirty = useMemo(() => {
    const current = [...selectedPermissionIds].sort().join(",");
    const initial = [...initialPermissionIds].sort().join(",");
    return current !== initial;
  }, [selectedPermissionIds, initialPermissionIds]);

  const refreshRoles = useCallback(async () => {
    const response = await ListarRolesAdmin();
    if (response.success) {
      setRoles(response.data || []);
      return response.data || [];
    }
    throw new Error(response.message || "No se pudo cargar roles");
  }, []);

  const refreshRoleDetail = useCallback(async (idrol) => {
    setDetailLoading(true);
    setDetailError("");
    try {
      const response = await ObtenerRolAdmin(idrol);
      if (!response.success) throw new Error(response.message);
      const detail = response.data;
      const ids = (detail.permissions || []).map((permission) => permission.idpermiso);
      setRoleDetail(detail);
      setSelectedPermissionIds(ids);
      setInitialPermissionIds(ids);
      setSelectedRoleId(idrol);
      return detail;
    } catch (error) {
      setDetailError(getApiError(error, "No se pudo cargar el rol"));
      setRoleDetail(null);
      return null;
    } finally {
      setDetailLoading(false);
    }
  }, []);

  const loadInitial = useCallback(async () => {
    if (initialLoadedRef.current) return;
    initialLoadedRef.current = true;
    setLoadingInitial(true);
    setInitialError("");
    try {
      const [rolesResponse, catalogResponse] = await Promise.all([
        ListarRolesAdmin(),
        ListarCatalogoPermisos(),
      ]);
      if (!rolesResponse.success) throw new Error(rolesResponse.message);
      if (!catalogResponse.success) throw new Error(catalogResponse.message);

      const loadedRoles = rolesResponse.data || [];
      setRoles(loadedRoles);
      setCatalog(catalogResponse.data?.permisos || []);
    } catch (error) {
      initialLoadedRef.current = false;
      setInitialError(getApiError(error, "No se pudo cargar roles y permisos"));
    } finally {
      setLoadingInitial(false);
    }
  }, []);

  useEffect(() => {
    if (!permissionsLoading && canView) {
      loadInitial();
    }
  }, [permissionsLoading, canView, loadInitial]);

  // RBAC-CUSTOM-ROLES-03B seccion H: unica fuente real de "clinicas
  // autorizadas del actor" -- GET /listar_clinicas, ya usado por UserCrud.js
  // para el mismo proposito. Carga perezosa (solo al abrir el formulario de
  // creacion/copia) para no disparar una llamada que la mayoria de actores
  // (sin roles.custom.create) nunca necesita.
  const loadAuthorizedClinics = async () => {
    setClinicsError("");
    try {
      const clinicas = await ListarClinicas();
      if (!clinicas) throw new Error("No se pudieron cargar las clínicas autorizadas");
      setAuthorizedClinics(clinicas);
    } catch (error) {
      setAuthorizedClinics([]);
      setClinicsError(getApiError(error, "No se pudieron cargar las clínicas autorizadas"));
    }
  };

  const openCreate = () => {
    setFormError("");
    setCopySource(null);
    setFormState({ open: true, mode: "create", role: null });
    loadAuthorizedClinics();
  };

  const openCopy = (role) => {
    setFormError("");
    setCopySource(role);
    setFormState({ open: true, mode: "create", role: null });
    loadAuthorizedClinics();
  };

  const openEdit = (role) => {
    openRoleModal(role, "edit");
  };

  const closeForm = () => {
    setFormState((current) => ({ ...current, open: false }));
    setCopySource(null);
  };

  // RBAC-CUSTOM-ROLES-03B seccion J/K: usado por RoleForm para precargar la
  // matriz al elegir "Rol base" -- lectura pura (GET /roles/:idrol), nunca
  // muta el rol fuente.
  const fetchRoleDetailForCopy = async (idrol) => {
    const response = await ObtenerRolAdmin(idrol);
    if (!response.success) throw new Error(response.message);
    return response.data;
  };

  const submitForm = async (payload) => {
    setFormLoading(true);
    setFormError("");
    try {
      const response =
        formState.mode === "create"
          ? await CrearRolAdmin(payload)
          : await ActualizarRolAdmin(formState.role.idrol, payload);
      if (!response.success) throw new Error(response.message);
      closeForm();
      const updatedRoles = await refreshRoles();
      const nextId = formState.mode === "create" ? response.data.idrol : formState.role.idrol;
      if (updatedRoles.some((role) => role.idrol === nextId)) {
        await refreshRoleDetail(nextId);
      }
      Swal.fire("Éxito", response.message, "success");
    } catch (error) {
      setFormError(getApiError(error, "No se pudo guardar el rol"));
    } finally {
      setFormLoading(false);
    }
  };

  const savePermissions = async () => {
    if (!roleDetail) return;
    setSavingPermissions(true);
    setMatrixError("");
    try {
      const response = await ReemplazarPermisosRol(roleDetail.idrol, selectedPermissionIds);
      if (!response.success) throw new Error(response.message);
      setInitialPermissionIds(selectedPermissionIds);
      await refreshRoleDetail(roleDetail.idrol);
      await refreshRoles();
      Swal.fire("Éxito", response.message, "success");
    } catch (error) {
      setMatrixError(getApiError(error, "No se pudo guardar permisos"));
    } finally {
      setSavingPermissions(false);
    }
  };

  const saveRoleChanges = async () => {
    if (!roleDetail) return;
    setSavingPermissions(true);
    setMatrixError("");
    try {
      let lastMessage = "Rol actualizado correctamente";
      const metadataPayload = {};
      if (!roleDetail.is_system && nombreEdit !== (roleDetail.nombre || "")) {
        metadataPayload.nombre = nombreEdit;
      }
      if (descripcionEdit !== (roleDetail.descripcion || "")) {
        metadataPayload.descripcion = descripcionEdit;
      }
      if (Object.keys(metadataPayload).length > 0) {
        const response = await ActualizarRolAdmin(roleDetail.idrol, metadataPayload);
        if (!response.success) throw new Error(response.message);
        lastMessage = response.message || lastMessage;
      }
      if (permissionsDirty && !isSystemLockedRole(roleDetail) && canAssignPermissionsCustom) {
        const response = await ReemplazarPermisosRol(roleDetail.idrol, selectedPermissionIds);
        if (!response.success) throw new Error(response.message);
        lastMessage = response.message || lastMessage;
      }
      setInitialPermissionIds(selectedPermissionIds);
      await refreshRoles();
      await refreshRoleDetail(roleDetail.idrol);
      Swal.fire("Éxito", lastMessage, "success");
      setDetailModal({ open: false, mode: "view" });
    } catch (error) {
      setMatrixError(getApiError(error, "No se pudo guardar cambios del rol"));
    } finally {
      setSavingPermissions(false);
    }
  };

  const openRoleModal = async (role, mode = "view") => {
    setDetailModal({ open: true, mode });
    setDetailTab("permissions");
    setMatrixError("");
    const detail = await refreshRoleDetail(role.idrol);
    setNombreEdit(detail?.nombre || "");
    setDescripcionEdit(detail?.descripcion || "");
  };

  const closeRoleModal = () => {
    setDetailModal({ open: false, mode: "view" });
    setRoleDetail(null);
    setSelectedRoleId(null);
    setUsers([]);
    setUsersLoadedRoleId(null);
    setSelectedUser(null);
    setEffectiveData(null);
    setDetailError("");
    setMatrixError("");
  };

  const deleteRole = async (role) => {
    try {
      const response = await EliminarRolAdmin(role.idrol);
      if (!response.success) throw new Error(response.message);
      const updatedRoles = await refreshRoles();
      const nextRole = updatedRoles.find((item) => item.idrol !== role.idrol);
      setSelectedRoleId(null);
      setRoleDetail(null);
      setUsers([]);
      setSelectedUser(null);
      if (nextRole) await refreshRoleDetail(nextRole.idrol);
      Swal.fire("Eliminado", response.message, "success");
    } catch (error) {
      Swal.fire("Error", getApiError(error, "No se pudo eliminar el rol"), "error");
    }
  };

  const changeRoleActiveState = async (role, nextActive) => {
    try {
      const response = await ActualizarRolAdmin(role.idrol, { is_active: nextActive });
      if (!response.success) throw new Error(response.message);
      await refreshRoles();
      await refreshRoleDetail(role.idrol);
      Swal.fire("Éxito", response.message, "success");
    } catch (error) {
      Swal.fire("Error", getApiError(error, nextActive ? "No se pudo activar el rol" : "No se pudo desactivar el rol"), "error");
    }
  };

  const loadRoleUsers = async (idrol) => {
    setUsersLoading(true);
    setUsersError("");
    try {
      const response = await ListarUsuariosRol(idrol);
      if (!response.success) throw new Error(response.message);
      setUsers(response.data || []);
      setUsersLoadedRoleId(idrol);
      setSelectedUser(null);
      setEffectiveData(null);
    } catch (error) {
      setUsersError(getApiError(error, "No se pudo cargar usuarios del rol"));
    } finally {
      setUsersLoading(false);
    }
  };

  const loadEffectivePermissions = async (user) => {
    setSelectedUser(user);
    setEffectiveLoading(true);
    setEffectiveError("");
    setEffectiveData(null);
    try {
      const response = await ObtenerPermisosEfectivosUsuario(user.iduser);
      if (!response.success) throw new Error(response.message);
      setEffectiveData(response.data);
    } catch (error) {
      const status = error?.response?.status;
      setEffectiveError(
        status === 404
          ? "Usuario no disponible o fuera de su alcance."
          : getApiError(error, "No se pudo cargar permisos efectivos")
      );
    } finally {
      setEffectiveLoading(false);
    }
  };

  const grantExtra = async (idpermiso) => {
    if (!selectedUser) return;
    setGrantLoading(true);
    setEffectiveError("");
    try {
      const response = await OtorgarPermisoExtra(selectedUser.iduser, idpermiso);
      if (!response.success) throw new Error(response.message);
      await loadEffectivePermissions(selectedUser);
    } catch (error) {
      setEffectiveError(getApiError(error, "No se pudo agregar permiso extra"));
    } finally {
      setGrantLoading(false);
    }
  };

  const revokeExtra = async (idpermiso, permissionName) => {
    if (!selectedUser) return;
    setRevokeLoadingName(permissionName);
    setEffectiveError("");
    try {
      const response = await RevocarPermisoExtra(selectedUser.iduser, idpermiso);
      if (!response.success) throw new Error(response.message);
      await loadEffectivePermissions(selectedUser);
    } catch (error) {
      setEffectiveError(getApiError(error, "No se pudo quitar permiso extra"));
    } finally {
      setRevokeLoadingName("");
    }
  };

  const filteredRoles = useMemo(() => {
    const term = searchRole.trim().toLowerCase();
    return roles.filter((role) => {
      const matchesSearch =
        !term ||
        String(role.nombre || "").toLowerCase().includes(term) ||
        String(role.clinica_nombre || "").toLowerCase().includes(term);
      const type = role.is_transition ? "transition" : role.is_system ? "system" : "custom";
      const matchesType = typeFilter === "all" || typeFilter === type;
      const status = role.is_active ? "active" : "inactive";
      const matchesStatus = statusFilter === "all" || statusFilter === status;
      return matchesSearch && matchesType && matchesStatus;
    });
  }, [roles, searchRole, typeFilter, statusFilter]);

  const columns = [
    {
      title: "Nombre",
      dataIndex: "nombre",
      key: "nombre",
      width: 220,
      render: (value, role) => (
        <button className="rp-link-button" onClick={() => openRoleModal(role, "view")}>
          {value}
        </button>
      ),
    },
    {
      title: "Tipo",
      key: "tipo",
      width: 170,
      render: (_, role) => (
        <Tag color={roleTypeColor(role)}>{getRoleTypeLabel(role)}</Tag>
      ),
    },
    { title: "Scope", dataIndex: "scope_type", key: "scope_type", width: 110 },
    {
      title: "Clínica",
      key: "clinica",
      width: 130,
      render: (_, role) => role.clinica_nombre || "—",
    },
    { title: "Usuarios", dataIndex: "users_count", key: "users_count", width: 90 },
    { title: "Permisos", dataIndex: "permissions_count", key: "permissions_count", width: 90 },
    {
      title: "Estado",
      key: "estado",
      width: 100,
      render: (_, role) => (
        <Tag color={role.is_active ? "success" : "default"}>
          {role.is_active ? "Activo" : "Inactivo"}
        </Tag>
      ),
    },
    {
      title: "Acciones",
      key: "actions",
      width: 210,
      render: (_, role) => {
        // RBAC-CUSTOM-ROLES-03B seccion M/N: un rol Sistema SIEMPRE se
        // administra con los permisos GLOBALES (roles.edit/delete); un rol
        // PERSONALIZADO (is_system=false) SIEMPRE con los roles.custom.* --
        // nunca se mezclan, y nunca se decide por nombre de rol.
        const locked = isSystemLockedRole(role);
        const canChange = canChangeCustomRole(role);
        const effectiveCanEdit = role.is_system ? canEdit : canEditCustom;
        const effectiveCanDelete = role.is_system ? canDelete : canDeleteCustom;
        const canCopyThis = canCreateCustom;
        const nextActiveState = !role.is_active;
        const activeActionLabel = role.is_active ? "Desactivar" : "Activar";
        const activeActionTooltip = locked
          ? "Rol de sistema protegido"
          : !effectiveCanEdit
            ? "Necesita roles.custom.edit"
            : `${activeActionLabel} rol personalizado`;
        return (
          <Space>
            <Tooltip title="Ver detalle">
              <Button size="small" icon={<EyeOutlined />} onClick={() => openRoleModal(role, "view")} />
            </Tooltip>
            <Tooltip title={canCopyThis ? "Copiar como rol personalizado" : "Necesita roles.custom.create"}>
              <Button
                size="small"
                icon={<CopyOutlined />}
                disabled={!canCopyThis}
                onClick={() => openCopy(role)}
              />
            </Tooltip>
            <Tooltip
              title={
                locked
                  ? "Rol de sistema protegido. Puedes verlo o copiarlo, pero no modificarlo directamente."
                  : !effectiveCanEdit
                    ? "Necesita roles.custom.edit"
                    : "Editar rol personalizado"
              }
            >
              <Button
                size="small"
                icon={<EditOutlined />}
                disabled={locked || !effectiveCanEdit}
                onClick={() => openEdit(role)}
              />
            </Tooltip>
            <Tooltip
              title={activeActionTooltip}
            >
              <Button
                size="small"
                aria-label={activeActionLabel}
                icon={<PoweroffOutlined />}
                disabled={locked || !canChange || !effectiveCanEdit}
                onClick={() => changeRoleActiveState(role, nextActiveState)}
              />
            </Tooltip>
            <Popconfirm
              title="Eliminar rol"
              description={
                role.users_count > 0
                  ? "El backend puede rechazarlo si tiene usuarios asignados."
                  : "Esta acción no cambia usuarios existentes."
              }
              okText="Eliminar"
              cancelText="Cancelar"
              disabled={locked || !canChange || !effectiveCanDelete}
              onConfirm={() => deleteRole(role)}
            >
              <Tooltip
                title={
                  locked
                    ? "Rol de sistema protegido"
                    : !effectiveCanDelete
                      ? "Necesita roles.custom.delete"
                      : "Eliminar rol personalizado"
                }
              >
                <Button
                  size="small"
                  danger
                  icon={<DeleteOutlined />}
                  disabled={locked || !canChange || !effectiveCanDelete}
                />
              </Tooltip>
            </Popconfirm>
          </Space>
        );
      },
    },
  ];

  if (permissionsLoading) {
    return (
      <div className="rp-centered">
        <Spin />
      </div>
    );
  }

  if (!canView) {
    return <Alert type="warning" message="No tiene permisos para ver Roles y permisos." showIcon />;
  }

  if (loadingInitial) {
    return (
      <div className="rp-centered">
        <Spin />
      </div>
    );
  }

  if (initialError) {
    return <Alert type="error" message={initialError} showIcon />;
  }

  return (
    <section className="rp-admin">
      <div className="rp-header">
        <div>
          <h5>Roles y permisos</h5>
          <span>Administra los roles y los permisos del sistema</span>
        </div>
        {canCreateCustom && (
          <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
            Nuevo rol
          </Button>
        )}
      </div>

      <div className="rp-role-list">
        <div className="rp-list-toolbar">
          <Input.Search
            allowClear
            placeholder="Buscar por nombre o clínica"
            value={searchRole}
            onChange={(event) => setSearchRole(event.target.value)}
            style={{ maxWidth: 300 }}
          />
          <Select
            value={typeFilter}
            onChange={setTypeFilter}
            style={{ width: 180 }}
            options={[
              { value: "all", label: "Todos los tipos" },
              { value: "system", label: "Sistema" },
              { value: "custom", label: "Personalizado" },
              { value: "transition", label: "Transición" },
            ]}
          />
          <Select
            value={statusFilter}
            onChange={setStatusFilter}
            style={{ width: 160 }}
            options={[
              { value: "all", label: "Todos los estados" },
              { value: "active", label: "Activo" },
              { value: "inactive", label: "Inactivo" },
            ]}
          />
        </div>
        <Table
          rowKey="idrol"
          columns={columns}
          dataSource={filteredRoles}
          size="small"
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showTotal: (total, range) => `Mostrando ${range[0]}-${range[1]} de ${total} roles`,
          }}
          scroll={{ x: 1080 }}
          rowClassName={(record) => (record.idrol === selectedRoleId ? "rp-selected-row" : "")}
          locale={{ emptyText: <Empty description="No hay roles" /> }}
        />
      </div>

      <Modal
        title={
          roleDetail ? (
            <Space wrap>
              <span>{detailModal.mode === "edit" ? "Editar rol" : roleDetail.nombre}</span>
              <Tag color={roleTypeColor(roleDetail)}>{getRoleTypeLabel(roleDetail)}</Tag>
            </Space>
          ) : "Rol"
        }
        open={detailModal.open}
        onCancel={closeRoleModal}
        width={960}
        destroyOnHidden
        footer={
          detailModal.mode === "edit"
            ? [
                <Button key="cancel" onClick={closeRoleModal}>
                  Cancelar
                </Button>,
                <Button key="save" type="primary" loading={savingPermissions} onClick={saveRoleChanges}>
                  Guardar cambios
                </Button>,
              ]
            : [
                <Button key="close" onClick={closeRoleModal}>
                  Cerrar
                </Button>,
              ]
        }
      >
        <div className="rp-detail-modal">
          {detailLoading ? (
            <div className="rp-centered">
              <Spin />
            </div>
          ) : detailError ? (
            <Alert type="error" message={detailError} showIcon />
          ) : !roleDetail ? (
            <Empty description="Seleccione un rol" />
          ) : (
            <>
              <div className="rp-detail-title">
                <div>
                  <h5>{roleDetail.nombre}</h5>
                  {detailModal.mode === "edit" ? (
                    <Space direction="vertical" style={{ width: "100%" }}>
                      <Input
                        value={nombreEdit}
                        onChange={(event) => setNombreEdit(event.target.value)}
                        placeholder="Nombre"
                        disabled={roleDetail.is_system}
                      />
                      <Input.TextArea
                        rows={2}
                        value={descripcionEdit}
                        onChange={(event) => setDescripcionEdit(event.target.value)}
                        placeholder="Descripción"
                      />
                    </Space>
                  ) : (
                    <span>{roleDetail.descripcion || "Sin descripción"}</span>
                  )}
                </div>
                <Space>
                  <Tag color={roleTypeColor(roleDetail)}>{getRoleTypeLabel(roleDetail)}</Tag>
                  {!roleDetail.is_system && roleDetail.clinica_nombre && (
                    <Tag color="green">{roleDetail.clinica_nombre}</Tag>
                  )}
                </Space>
              </div>
              {isSystemLockedRole(roleDetail) ? (
                <Alert
                  className="mb-3"
                  type="info"
                  showIcon
                  message="Rol de sistema protegido. Puedes verlo o copiarlo para crear un rol personalizado, pero no modificarlo directamente."
                />
              ) : detailModal.mode === "edit" ? (
                <Alert
                  className="mb-3"
                  type="warning"
                  showIcon
                  message="Los cambios en los permisos afectarán a todos los usuarios que tengan asignado este rol."
                />
              ) : null}

              <div className="rp-kpi-row">
                <div><strong>{roleDetail.users_count || 0}</strong><span>Usuarios</span></div>
                <div><strong>{roleDetail.permissions_count || selectedPermissionIds.length}</strong><span>Permisos</span></div>
                <div><strong>{roleDetail.scope_type || "—"}</strong><span>Scope</span></div>
                <div><strong>{roleDetail.clinica_nombre || "—"}</strong><span>Clínica</span></div>
              </div>

              <Tabs
                activeKey={detailTab}
                items={[
                  {
                    key: "permissions",
                    label: "Permisos",
                    children: (
                      <RolePermissionsMatrix
                        catalog={catalog}
                        selectedIds={selectedPermissionIds}
                        readOnly={
                          detailModal.mode !== "edit" ||
                          isSystemLockedRole(roleDetail) ||
                          !(roleDetail.is_system ? canAssignPermissions : canAssignPermissionsCustom)
                        }
                        dirty={permissionsDirty}
                        saving={savingPermissions}
                        error={matrixError}
                        onChange={setSelectedPermissionIds}
                        onSave={detailModal.mode === "edit" ? saveRoleChanges : savePermissions}
                      />
                    ),
                  },
                  {
                    key: "users",
                    label: (
                      <span>
                        <UserOutlined /> Usuarios
                      </span>
                    ),
                    children: (
                      <RoleUsers
                        users={usersLoadedRoleId === roleDetail.idrol ? users : []}
                        selectedUser={selectedUser}
                        loading={usersLoading}
                        error={usersError}
                        onSelectUser={loadEffectivePermissions}
                        effectiveProps={{
                          data: effectiveData,
                          catalog,
                          loading: effectiveLoading,
                          error: effectiveError,
                          canAssignPermissions: canManageUserExtras,
                          grantLoading,
                          revokeLoadingName,
                          onGrant: grantExtra,
                          onRevoke: revokeExtra,
                        }}
                      />
                    ),
                  },
                ]}
                onChange={(key) => {
                  setDetailTab(key);
                  if (key === "users" && usersLoadedRoleId !== roleDetail.idrol) {
                    loadRoleUsers(roleDetail.idrol);
                  }
                }}
              />
            </>
          )}
        </div>
      </Modal>

      <RoleForm
        open={formState.open}
        mode={formState.mode}
        role={formState.role}
        roles={roles}
        authorizedClinics={authorizedClinics}
        clinicsError={clinicsError}
        catalog={catalog}
        fetchRoleDetail={fetchRoleDetailForCopy}
        initialCopySource={copySource}
        onCancel={closeForm}
        onSubmit={submitForm}
        loading={formLoading}
        error={formError}
      />
    </section>
  );
}
